import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useLibraryStore, isOwnPoi, isOwnTag } from "../store/libraryStore";
import { useUIStore } from "../store/uiStore";
import { Button } from "../components/Button";
import { TagEditorModal } from "../components/TagEditorModal";
import { POIEditorModal } from "../components/POIEditorModal";
import { TagIcon } from "../components/TagIcon";
import type { Tag } from "@poi-planner/shared";
import clsx from "clsx";
import { themeColors } from "../theme/colors";
import { CategoryTagManagerModal } from "../components/CategoryTagManagerModal";

type TagSortMode = "usage" | "alpha";

export function TagsPage() {
  const navigate = useNavigate();
  const {
    tags: allTags,
    pois: allPois,
    createTag,
    updateTag,
    deleteTag,
  } = useLibraryStore();
  // Tag management is own-library-only: foreign tags (visible via a shared
  // trip) gain no edit/delete affordances here, and usage counts only
  // reflect the caller's own POIs so cross-user tag-name collisions can't
  // inflate a count.
  const tags = useMemo(() => allTags.filter(isOwnTag), [allTags]);
  const pois = useMemo(() => allPois.filter(isOwnPoi), [allPois]);

  const [search, setSearch] = useState("");
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tagSortMode, setTagSortMode] = useState<TagSortMode>("usage");
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const openPOIEditor = useUIStore((s) => s.openPOIEditor);

  // Calculate usage count for each tag
  const tagUsageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tag of tags) {
      // Count POIs that have this tag in their string tags array
      // OR have this tag as their primaryTagId
      counts[tag.id] = pois.filter(
        (p) => p.tags.includes(tag.id) || p.primaryTagId === tag.id,
      ).length;
    }
    return counts;
  }, [tags, pois]);

  // Filter and sort tags by search
  const filteredTags = useMemo(() => {
    const query = search.toLowerCase().trim();
    const result = query
      ? tags.filter((t) => t.name.toLowerCase().includes(query))
      : [...tags];

    // Sort based on mode
    if (tagSortMode === "usage") {
      result.sort((a, b) => {
        const countDiff =
          (tagUsageCounts[b.id] ?? 0) - (tagUsageCounts[a.id] ?? 0);
        if (countDiff !== 0) return countDiff;
        return a.name.localeCompare(b.name);
      });
    } else {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [tags, search, tagSortMode, tagUsageCounts]);

  // Get selected tag
  const selectedTag = useMemo(() => {
    if (!selectedTagId) return null;
    return tags.find((t) => t.id === selectedTagId) ?? null;
  }, [tags, selectedTagId]);

  // Get POIs using this tag as their primary (map icon) tag
  const primaryTagPois = useMemo(() => {
    if (!selectedTag) return [];
    return pois.filter((p) => p.primaryTagId === selectedTag.id);
  }, [selectedTag, pois]);

  // Get POIs that have this tag but use a different primary tag
  const secondaryTagPois = useMemo(() => {
    if (!selectedTag) return [];
    return pois.filter(
      (p) =>
        p.tags.includes(selectedTag.id) && p.primaryTagId !== selectedTag.id,
    );
  }, [selectedTag, pois]);

  const handleEditPoi = (poiId: string) => {
    openPOIEditor("edit", poiId);
  };

  const handleCreateTag = async (data: Omit<Tag, "id">) => {
    return createTag(data);
  };

  const handleUpdateTag = async (data: Omit<Tag, "id">) => {
    if (!editingTag) return;
    await updateTag(editingTag.id, data);
  };

  const handleDeleteTag = async () => {
    if (!editingTag) return;
    await deleteTag(editingTag.id);
    if (selectedTagId === editingTag.id) {
      setSelectedTagId(null);
    }
  };

  return (
    <div className="h-screen bg-page flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white border-b border-border shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/")}
              variant="icon"
              icon="arrow_back"
              title="Back to trips"
              aria-label="Back to trips"
            />
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-2xl text-primary">
                label
              </span>
              <h1 className="text-lg font-bold text-primary tracking-tight">
                Tag Manager
              </h1>
            </div>
          </div>
          <Button
            variant="secondary"
            icon="account_tree"
            onClick={() => setShowCategoryManager(true)}
          >
            Category tags
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Tag List */}
        <div className="w-96 shrink-0 flex flex-col bg-white border-r border-border">
          {/* Search */}
          <div className="p-4 border-b border-border">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-lg text-tertiary">
                search
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tags..."
                className="input-base pl-10"
              />
            </div>

            {/* Sort & Count */}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs text-secondary">
                {filteredTags.length === tags.length
                  ? `${tags.length} ${tags.length === 1 ? "tag" : "tags"}`
                  : `${filteredTags.length} of ${tags.length} tags`}
              </div>
              <select
                value={tagSortMode}
                onChange={(e) => setTagSortMode(e.target.value as TagSortMode)}
                className="text-xs px-2 py-1 rounded-md border border-border bg-white text-secondary cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
              >
                <option value="usage">Most Used</option>
                <option value="alpha">A → Z</option>
              </select>
            </div>
          </div>

          {/* Tag List */}
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {filteredTags.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6">
                {tags.length === 0 ? (
                  <>
                    <div className="flex items-center justify-center size-16 rounded-2xl bg-slate-100 mb-4">
                      <span className="material-symbols-outlined text-3xl text-slate-400">
                        label_off
                      </span>
                    </div>
                    <h3 className="text-sm font-bold text-primary mb-1">
                      No tags yet
                    </h3>
                    <p className="text-xs text-secondary text-center mb-4">
                      Create tags to organize your POIs and customize map
                      markers with icons.
                    </p>
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      variant="primary"
                    >
                      Create First Tag
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-3xl text-slate-300 mb-2">
                      search_off
                    </span>
                    <p className="text-sm text-tertiary">
                      No tags match "{search}"
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="p-2">
                {filteredTags.map((tag) => {
                  const isSelected = selectedTagId === tag.id;
                  const count = tagUsageCounts[tag.id] ?? 0;

                  return (
                    <div
                      key={tag.id}
                      role="button"
                      tabIndex={0}
                      aria-pressed={isSelected}
                      onClick={() => setSelectedTagId(tag.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedTagId(tag.id);
                        }
                      }}
                      className={clsx(
                        "group w-full flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all text-left border-none",
                        isSelected
                          ? "bg-slate-100"
                          : "bg-transparent hover:bg-slate-50",
                      )}
                    >
                      {/* Icon */}
                      <div
                        className="flex items-center justify-center size-10 rounded-lg shrink-0 transition-transform group-hover:scale-105"
                        style={{
                          backgroundColor: tag.color || themeColors.primary,
                        }}
                      >
                        <TagIcon icon={tag.icon} size={20} whiteTint />
                      </div>

                      {/* Name & Count */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-primary truncate">
                          {tag.name}
                        </div>
                        <div className="text-xs text-tertiary">
                          {count} {count === 1 ? "POI" : "POIs"}
                        </div>
                      </div>

                      {/* Edit button */}
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTag(tag);
                        }}
                        variant="icon"
                        icon="edit"
                        className={clsx(
                          "reveal-on-card-hover",
                          isSelected && "opacity-100",
                        )}
                        aria-label={`Edit ${tag.name}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sticky Action Footer */}
          <div className="shrink-0 p-4 border-t border-border bg-white">
            <Button
              onClick={() => setShowCreateModal(true)}
              variant="primary"
              size="lg"
              icon="add"
              fullWidth
            >
              New Tag
            </Button>
          </div>
        </div>

        {/* Right Panel - Tag Details */}
        <div className="flex-1 flex flex-col p-6 overflow-y-auto">
          {selectedTag ? (
            <div className="max-w-2xl">
              {/* Tag header */}
              <div className="flex items-start gap-5 mb-8">
                <div
                  className="flex items-center justify-center size-20 rounded-2xl shrink-0"
                  style={{
                    backgroundColor: selectedTag.color || themeColors.primary,
                  }}
                >
                  <TagIcon icon={selectedTag.icon} size={44} whiteTint />
                </div>
                <div className="flex-1 pt-1">
                  <h2 className="text-2xl font-bold text-primary mb-1">
                    {selectedTag.name}
                  </h2>
                  <p className="text-sm text-secondary mb-3">
                    Used by {tagUsageCounts[selectedTag.id] ?? 0}{" "}
                    {(tagUsageCounts[selectedTag.id] ?? 0) === 1
                      ? "POI"
                      : "POIs"}
                  </p>
                  <Button
                    onClick={() => setEditingTag(selectedTag)}
                    variant="secondary"
                    icon="edit"
                  >
                    Edit Tag
                  </Button>
                </div>
              </div>

              {/* POIs using this tag */}
              <div className="flex flex-col gap-6">
                {/* Primary Tag POIs */}
                <div>
                  <h3 className="text-sm font-bold text-primary mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg text-blue-600">
                      star
                    </span>
                    Primary Tag
                  </h3>
                  <p className="text-xs text-tertiary mb-3">
                    POIs using this tag for their map icon
                  </p>

                  {primaryTagPois.length === 0 ? (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-border">
                      <span className="material-symbols-outlined text-2xl text-slate-300">
                        star_border
                      </span>
                      <p className="text-sm text-tertiary">
                        No POIs are using this as their primary tag
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {primaryTagPois.map((poi) => (
                        <button
                          key={poi.id}
                          onClick={() => handleEditPoi(poi.id)}
                          className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-blue-200 text-left transition-all hover:border-blue-300 hover:shadow-sm cursor-pointer"
                        >
                          <div
                            className="flex items-center justify-center size-9 rounded-lg shrink-0"
                            style={{
                              backgroundColor:
                                selectedTag.color || themeColors.primary,
                            }}
                          >
                            <TagIcon
                              icon={selectedTag.icon}
                              size={18}
                              whiteTint
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-primary truncate">
                              {poi.name}
                            </div>
                          </div>
                          <span className="material-symbols-outlined text-lg text-tertiary">
                            chevron_right
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Also Tagged POIs */}
                <div>
                  <h3 className="text-sm font-bold text-primary mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">
                      label
                    </span>
                    Also Tagged
                  </h3>
                  <p className="text-xs text-tertiary mb-3">
                    POIs with this tag but using a different primary tag
                  </p>

                  {secondaryTagPois.length === 0 ? (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-border">
                      <span className="material-symbols-outlined text-2xl text-slate-300">
                        label_off
                      </span>
                      <p className="text-sm text-tertiary">
                        No other POIs have this tag
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {secondaryTagPois.map((poi) => {
                        // Find the POI's primary tag for the icon
                        const primaryTag = poi.primaryTagId
                          ? tags.find((t) => t.id === poi.primaryTagId)
                          : null;

                        return (
                          <button
                            key={poi.id}
                            onClick={() => handleEditPoi(poi.id)}
                            className="flex items-center gap-3 p-3 bg-white rounded-xl border border-border text-left transition-all hover:border-slate-300 hover:shadow-sm cursor-pointer"
                          >
                            <div
                              className="flex items-center justify-center size-9 rounded-lg shrink-0"
                              style={{
                                backgroundColor:
                                  primaryTag?.color || themeColors.slate500,
                              }}
                            >
                              <TagIcon
                                icon={primaryTag?.icon || "phosphor:map-pin"}
                                size={18}
                                whiteTint
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-primary truncate">
                                {poi.name}
                              </div>
                              {primaryTag && (
                                <div className="text-xs text-tertiary">
                                  Primary: {primaryTag.name}
                                </div>
                              )}
                            </div>
                            <span className="material-symbols-outlined text-lg text-tertiary">
                              chevron_right
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <div className="flex items-center justify-center size-24 rounded-3xl bg-slate-100 mb-6">
                <span className="material-symbols-outlined text-5xl text-slate-300">
                  label
                </span>
              </div>
              <h2 className="text-lg font-bold text-primary mb-2">
                Select a tag
              </h2>
              <p className="text-sm text-secondary max-w-sm">
                Choose a tag from the list to see details and which POIs are
                using it.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <TagEditorModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateTag}
      />

      {/* Edit Modal */}
      {editingTag && (
        <TagEditorModal
          open={!!editingTag}
          onClose={() => setEditingTag(null)}
          tag={editingTag}
          onSave={handleUpdateTag}
          onDelete={handleDeleteTag}
          usageCount={tagUsageCounts[editingTag.id] ?? 0}
        />
      )}

      {/* POI Editor Modal */}
      <POIEditorModal />
      <CategoryTagManagerModal
        open={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
      />
    </div>
  );
}
